import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import { Visibility } from '../../components/global/Visibility';
import { PdpRespSimilarItem } from './PdpRespSimilarItem/PdpRespSimilarItem';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { Button } from '../../global/Button/Button';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';
import { moreForYourProject, showMoreForProject, showLessForProject } from '../pdpMessages';

// redux actions
import { hasFiredComplementaryItemsImpressions } from '../../actions/pdpRespSimilarItemsActions';

// styles
import styles from './PdpRespComplementaryItemsGrid.scss';

// helpers
import get from 'lodash.get';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import {
    trackComplementaryItemClick,
    trackComplementaryItemImpressions,
    trackComplementaryGridViewEvent,
    trackComplementaryGridToggleExpansionEvent,
    trackComplementaryItemClickEvent,
} from '../tracking/similarItemsTracking';

const DISPLAY_COUNT_COLLAPSED = 4;
const DISPLAY_COUNT_EXPANDED = 12;

class PdpRespComplementaryItemsGridComponent extends React.Component {
    constructor(props) {
        super(props);

        this.onClickItem = this.onClickItem.bind(this);
        this.onContentVisible = this.onContentVisible.bind(this);
        this.onChangeExpand = this.onChangeExpand.bind(this);
        this.getGridItems = this.getGridItems.bind(this);
        this.canExpandList = this.canExpandList.bind(this);

        this.state = {
            isContentVisible: false,
            isExpanded: false,
            hasFiredAdditionalImpressions: false,
        };

        this.headerRef = React.createRef();
    }
    componentDidUpdate() {
        if (
            this.state.isContentVisible &&
            !this.props.complementaryItemsState.hasFiredImpressions
        ) {
            trackComplementaryGridViewEvent();

            if (get(this.props, 'item.complementaryItems.edges[0].node.ecommerceTrackingParams')) {
                trackComplementaryItemImpressions(this.getGridItems());
            }
            this.props.updateHasFiredImpressions();
        }
    }
    onContentVisible() {
        this.setState({
            isContentVisible: true,
        });
    }
    onClickItem(index) {
        trackComplementaryItemClick(index, this.getGridItems()[index]);
        trackComplementaryItemClickEvent();
    }
    getGridItems() {
        const gridItems = get(this.props, 'item.complementaryItems.edges') || [];
        const itemsToShow = this.state.isExpanded
            ? DISPLAY_COUNT_EXPANDED
            : DISPLAY_COUNT_COLLAPSED;
        return gridItems.slice(0, itemsToShow);
    }
    canExpandList() {
        const gridItems = get(this.props, 'item.complementaryItems.edges') || [];
        return gridItems.length > DISPLAY_COUNT_COLLAPSED;
    }
    onChangeExpand(nextExpandedState) {
        const { hasFiredAdditionalImpressions } = this.state;
        return () => {
            let nextState = { isExpanded: nextExpandedState };
            let gridItems;
            if (nextExpandedState && !hasFiredAdditionalImpressions) {
                gridItems = (get(this.props, 'item.complementaryItems.edges') || []).slice(
                    DISPLAY_COUNT_COLLAPSED,
                    DISPLAY_COUNT_EXPANDED
                );
                nextState = { ...nextState, hasFiredAdditionalImpressions: !!gridItems.length };
            }

            trackComplementaryGridToggleExpansionEvent(nextExpandedState, gridItems);
            this.setState(nextState, () => {
                if (!nextExpandedState && window && this.headerRef.current) {
                    this.headerRef.current.scrollIntoView({
                        behavior: 'smooth',
                        inline: 'center',
                    });
                }
            });
        };
    }
    render() {
        const gridItems = this.getGridItems();

        if (!gridItems.length) {
            return null;
        }

        const { isContentVisible, isExpanded } = this.state;
        const { isDark, viewer, currency, user, showLightBox } = this.props;
        const { isClient } = this.props.relayVariables;
        const favoritesProviderProps = {
            location: locations.SIMILAR_ITEM_GRID,
            disable: !isClient || !isContentVisible,
            userId: user && user.serviceId ? user.serviceId : null,
            environment: this.props.relay.environment,
            itemIds: gridItems.map(({ node }) => node.serviceId),
        };

        return (
            <PdpRespSimilarItemsContainer
                isDark={isDark}
                hideBar={isDark}
                header={<div ref={this.headerRef}>{moreForYourProject}</div>}
                dataTn="pdp-similar-recommendation-grid"
            >
                <Visibility onContentVisible={this.onContentVisible} />
                <FavoritesProvider {...favoritesProviderProps}>
                    {/* favorites  - passed to PdpRespFavoritingItem in PdpRespSimilarItem */}
                    {({ props: favorites }) => (
                        <QuickViewProvider>
                            {/* quickView - passed to PdpRespSimilarItemQuickView in PdpRespSimilarItem */}
                            {({ quickView }) => {
                                const itemProps = {
                                    isClient,
                                    quickView,
                                    favorites,
                                    onClick: this.onClickItem,
                                    user,
                                    showLightBox,
                                };

                                return (
                                    <div className="rowFlex">
                                        {gridItems.map(({ node }, index) => (
                                            <div
                                                key={node.serviceId}
                                                className="colLg3 colMd6 colXs6"
                                            >
                                                {/* Grid of "Complete the Look" items */}
                                                <PdpRespSimilarItem
                                                    item={node}
                                                    index={index}
                                                    viewer={viewer}
                                                    currency={currency}
                                                    {...itemProps}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                );
                            }}
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
                {this.canExpandList() && (
                    <div className={styles.viewAllWrapper}>
                        {isExpanded ? (
                            <Button onClick={this.onChangeExpand(false)} size="medium" isSecondary>
                                {showLessForProject}
                            </Button>
                        ) : (
                            <Button onClick={this.onChangeExpand(true)} size="medium" isSecondary>
                                {showMoreForProject}
                            </Button>
                        )}
                    </div>
                )}
            </PdpRespSimilarItemsContainer>
        );
    }
}

PdpRespComplementaryItemsGridComponent.defaultProps = {
    isDark: true,
    showLightBox: true,
};

PdpRespComplementaryItemsGridComponent.propTypes = {
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        showSimilarItems: PropTypes.bool,
        isClient: PropTypes.bool.isRequired,
    }).isRequired,
    item: PropTypes.object.isRequired,
    isDark: PropTypes.bool,
    showLightBox: PropTypes.bool,
    relay: PropTypes.shape({
        environment: PropTypes.object.isRequired,
    }).isRequired,
    currency: PropTypes.string,
    user: PropTypes.object,

    // redux
    complementaryItemsState: PropTypes.shape({
        hasFiredImpressions: PropTypes.bool.isRequired,
    }).isRequired,
    updateHasFiredImpressions: PropTypes.func.isRequired,
    viewer: PropTypes.object,
};

// Redux Map Functions
function mapStateToProps({ similarItems }) {
    return {
        complementaryItemsState: {
            hasFiredImpressions: similarItems.ecommerceImpressions.hasFiredComplementaryItems,
        },
    };
}
function mapDispatchToProps(dispatch) {
    return {
        updateHasFiredImpressions: () => dispatch(hasFiredComplementaryItemsImpressions()),
    };
}

export const PdpRespComplementaryItemsGrid = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(PdpRespComplementaryItemsGridComponent),
    {
        viewer: graphql`
            fragment PdpRespComplementaryItemsGrid_viewer on Viewer {
                ...PdpRespSimilarItem_viewer
            }
        `,
        item: graphql`
            fragment PdpRespComplementaryItemsGrid_item on Item {
                complementaryItems(first: 12) @include(if: $isTrade) {
                    edges {
                        node {
                            title
                            serviceId
                            categoryPath
                            categoryCode
                            browseUrl
                            ecommerceTrackingParams(userId: $userId) @include(if: $isClient)
                            ...PdpRespSimilarItem_item
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpRespComplementaryItemsGrid_user on User {
                serviceId
                ...PdpRespSimilarItem_user
            }
        `,
    }
);
